import { PtLuxonDate } from './date.models';
import { ApiEntityModel } from '../../_foundation/models/base.models';
import { DeepWellTieIn } from '../../models/metadata/metadata.models';
import { FieldPositioningEditor } from '../field-positioning/field-positioning.models';
import { OnSiteMovementEditor } from '../on-site-movement/on-site-movement.models';
import { LocationOption, SetupChangeLogRecord } from '../setup/setup.models';

export class JackingActivityType {
  id: string;
  tag: string;
  name: string;
}

export enum JackingActivityTypes {
  Arrival = 'arrival',
  Departure = 'departure'
}

export class JackingActivityStatus {
  id: string;
  tag: string;
  name: string;
}

export enum JackingActivityStatuses {
  NotStarted = 'notStarted',
  InProgress = 'inProgress',
  Complete = 'complete',
  Cancelled = 'cancelled'
}

export class JackingActivityDeepWellEditor extends ApiEntityModel<number> {
  deepWellTieInLeg3: number;
  deepWellTieInLeg3LiftAt: number;
  deepWellTieInLeg4: number;
  deepWellTieInLeg4LiftAt: number;
}

export class JackingActivityEditor extends ApiEntityModel<number> {
  localId: number;
  primaryOperationId: number;
  localPrimaryOperationId: number;
  secondaryOperationId: number;
  localSecondaryOperationId: number;
  startDate: PtLuxonDate;
  startDay: number;
  startOffset: number;
  endDate: PtLuxonDate;
  endDay: number;
  endOffset: number;
  endTimeConfirmed: boolean;
  lastModifiedDate: PtLuxonDate;
  lastModifiedBy: string;
  comment: string;
  type: JackingActivityType = new JackingActivityType();
  status: JackingActivityStatus = new JackingActivityStatus();
  events: JackingActivityEventEditor[] = [];
  projectInfo: JackingActivityProjectEditor = new JackingActivityProjectEditor();
  signatures: JackingActivitySignatureEditor = new JackingActivitySignatureEditor();
  locationInfo: JackingActivityLocationEditor = new JackingActivityLocationEditor();
  vesselSetup: JackingActivityVesselSetupEditor = new JackingActivityVesselSetupEditor();
  shockPadInfo: JackingActivityShockPadEditor = new JackingActivityShockPadEditor();
  finalJackingInfo: JackingActivityfinalDataEditor = new JackingActivityfinalDataEditor();
  legInfo: JackingActivityLegSummaryEditor = new JackingActivityLegSummaryEditor();
  deepWellInfo: JackingActivityDeepWellEditor = new JackingActivityDeepWellEditor();
  confirmed: boolean;
  completionNotes: string;
  cancellationNotes: string;
  cancellationTime: PtLuxonDate;
  fieldPositioningEntityId: number;
  // Non-API Properties
  showStartDate: string;
  showEndDate: string;
  endTimeToShow: string;
  showLastModifiedDate: string;
  activityTypeName: string;
  activityStatusName: string;

  // used locally for loading indicator in the grid
  viewFormLoading?: boolean;
  generateFormLoading?: boolean;
}

export class JackingActivityEventEditor extends ApiEntityModel<number> {
  guid: string;
  order: number; // Lowest comes first
  description: string;
  time: PtLuxonDate;
  craneSlewBreakLifted: boolean;
  airGap: number;
  waterDepth: number;
  waterTightIntegrityChecked_T2: boolean;
  waterTightIntegrityChecked_ER: boolean;
  jackingEventCollectionType: JackingActivityEventCollectionType;
  davitPump1Stowed: boolean;
  davitPump2Stowed: boolean;
  davitPump3Stowed: boolean;
  stdbSeachestSeaWaterSuctionId: number;
  portBufferHighSeaWaterSuctionId: number;
  portBufferLowSeaWaterSuctionId: number;
  holdMinutes: number;
  leg1: JackingActivityEventPerLegEditor = new JackingActivityEventPerLegEditor();
  leg2: JackingActivityEventPerLegEditor = new JackingActivityEventPerLegEditor();
  leg3: JackingActivityEventPerLegEditor = new JackingActivityEventPerLegEditor();
  leg4: JackingActivityEventPerLegEditor = new JackingActivityEventPerLegEditor();
  isLinkedToStep: boolean;
  timeConfirmed: boolean;
  // local properties to determine edit functionality
  timeToShow: string;
  timeOffset: number;
  canEditValues: boolean;
  shouldUpdatePrimaryOperationStartTime: boolean;
  shouldUpdatePrimaryOperationEndTime: boolean;
  shouldUpdateSecondaryOperationStartTime: boolean;
  shouldUpdateSecondaryOperationEndTime: boolean;
  showTimeConfirmation: boolean;
}

export class JackingActivityEventPerLegEditor extends ApiEntityModel<number> {
  load: number;
  ext: number;
  r: number;
  p: number;
  d: number;
  penetration: number;
  waterDepth: number;
  waterTightIntegrityChecked: boolean;
  jettingPressure: number;
}

export class JackingActivityfinalDataPerLegEditor extends ApiEntityModel<number> {
  finalPenetration: number;
  differenceFromExpected: number;
  load: number;
  ext: number;
  deepWellTieInDepth: number;
  deepWellTieInNumber: number;
  r: number;
  p: number;
  d: number;
  northing: number;
  easting: number;
}

export class JackingActivityfinalDataEditor extends ApiEntityModel<number> {
  leg1: JackingActivityfinalDataPerLegEditor = new JackingActivityfinalDataPerLegEditor();
  leg2: JackingActivityfinalDataPerLegEditor = new JackingActivityfinalDataPerLegEditor();
  leg3: JackingActivityfinalDataPerLegEditor = new JackingActivityfinalDataPerLegEditor();
  leg4: JackingActivityfinalDataPerLegEditor = new JackingActivityfinalDataPerLegEditor();
  davitPump1Used: boolean;
  davitPump2Used: boolean;
  davitPump3Used: boolean;
  davitSeajetDeployedTo: number;
  seajet1Used: boolean;
  seajet2Used: boolean;
  seajet3Used: boolean;
  seajet4Used: boolean;
  depthDeployed: string;
  finalAirGap: number;
  notes: string;
  northingCRP: number;
  eastingCRP: number;
  nothingDistanceToAsset: number;
  eastingDistanceToAsset: number;

  finalLegValuesTime: PtLuxonDate;
  finalLegValuesTimeToShow: string;
  finalLegValuesTimeOffset: number;

  differenceFromExpectedTime: PtLuxonDate;
  differenceFromExpectedTimeToShow: string;
  differenceFromExpectedTimeOffset: number;

  finalPenetrationsTime: PtLuxonDate;
  finalPenetrationsTimeToShow: string;
  finalPenetrationsTimeOffset: number;

  deepWellTieInDepthTime: PtLuxonDate;
  deepWellTieInDepthTimeToShow: string;
  deepWellTieInDepthTimeOffset: number;

  deepWellTieInNumberTime: PtLuxonDate;
  deepWellTieInNumberTimeToShow: string;
  deepWellTieInNumberTimeOffset: number;

  davitPumpUsedTime: PtLuxonDate;
  davitPumpUsedTimeToShow: string;
  davitPumpUsedTimeOffset: number;

  seajetsUsedTime: PtLuxonDate;
  seajetsUsedTimeToShow: string;
  seajetsUsedTimeOffset: number;

  finalAirGapTime: PtLuxonDate;
  finalAirGapTimeToShow: string;
  finalAirGapTimeOffset: number;
  finalAirGapTimeConfirmed: boolean;
}


export class JackingActivityLegSummaryEditor extends ApiEntityModel<number> {
  leg1: JackingActivityLegSummaryPerLegEditor = new JackingActivityLegSummaryPerLegEditor();
  leg2: JackingActivityLegSummaryPerLegEditor = new JackingActivityLegSummaryPerLegEditor();
  leg3: JackingActivityLegSummaryPerLegEditor = new JackingActivityLegSummaryPerLegEditor();
  leg4: JackingActivityLegSummaryPerLegEditor = new JackingActivityLegSummaryPerLegEditor();
}

export class JackingActivityLegSummaryPerLegEditor extends ApiEntityModel<number> {
  // Max Pulling Load
  legMaxPullingLoad: number;
  legMaxPullingloadTime: PtLuxonDate;
  legMaxPullingloadTimeToShow: string;
  legMaxPullingLoadTimeOffset: number;
  // Free In Holes
  legFreeInHolesExt: number;
  legFreeInHolesExtTime: PtLuxonDate;
  legFreeInHolesExtTimeToShow: string;
  legFreeInHolesExtTimeOffset: number;
  // Clear of Seabed
  clearOfSeaBedExt: number;
  clearOfSeaBedExtTime: PtLuxonDate;
  clearOfSeaBedExtTimeToShow: string;
  clearOfSeabedExtTimeOffset: number;
}

export class JackingActivityLocationEditor extends ApiEntityModel<number> {
  rangeOfTide: number;
  surveyedCD: number;
  seaStateId: number;
  note: string;
  hW1: string;
  hW2: string;
  lW1: string;
  lW2: string;
  waterDepth: number;
}

export class JackingActivityProjectEditor extends ApiEntityModel<number> {
  vesselName: string;
  vesselClassId: number;
  lastLocationId: number;
  lastLocation: LocationOption; // used for the ionic-selectable library - as it requires a model for the ngmodel not just an id
  currentLocationId: number;
  nextLocationId: number;
  nextLocation: LocationOption; // used for the ionic-selectable library - as it requires a model for the ngmodel not just an id
  operationNumber: string;
  distanceTravelledToCurrentLocation: number;
  soilType: string;
  lowerExpectedPenetration: number;
  upperExpectedPenetration: number;
  hs: number;
  period: number;
  maxPreLoad: number;
  lastAirGap: number;
  lowerArrivalPenetration: number;
  upperArrivalPenetration: number;
  deepWellTieInsUsed: DeepWellTieIn[] = [];
  deepWellTieInsOtherDetails: string; // used if the user selects 'other' for deep well tie-ins
}

export class JackingActivityShockPadEditor extends ApiEntityModel<number> {
  notes: string;
  otherPosses: string;
  otherPOS: string;
  leg1: JackingActivityShockPadPerLegEditor = new JackingActivityShockPadPerLegEditor();
  leg2: JackingActivityShockPadPerLegEditor = new JackingActivityShockPadPerLegEditor();
  leg3: JackingActivityShockPadPerLegEditor = new JackingActivityShockPadPerLegEditor();
  leg4: JackingActivityShockPadPerLegEditor = new JackingActivityShockPadPerLegEditor();
}

export class JackingActivityShockPadPerLegEditor extends ApiEntityModel<number> {
  existingPenetration: number;
  usedShockPads: JackingActivityUsedShockPadEditor[] = [];
}

export class JackingActivityUsedShockPadEditor extends ApiEntityModel<number> {
  shockPadNumber: number;
}

export class JackingActivitySignatureEditor extends ApiEntityModel<number> {
  jackingEngineer1Name: string;
  jackingEngineer2Name: string;
}

export class JackingActivityVesselSetupEditor extends ApiEntityModel<number> {
  calcElWt: number;
  draft: number;
  trim: number;
  list: number;
  lcg: number;
  tcg: number;
}

// Local shock pad classes for binding
export class LocalShockPadInfo {
  leg1: LocalShockPadLeg = new LocalShockPadLeg();
  leg2: LocalShockPadLeg = new LocalShockPadLeg();
  leg3: LocalShockPadLeg = new LocalShockPadLeg();
  leg4: LocalShockPadLeg = new LocalShockPadLeg();
}

export class LocalShockPadLeg {
  usedShockPads: LocalShockPadUsed[] = [];
}

export class LocalShockPadUsed {
  shockPadNumber: number;
  isUsed: boolean;
}

export enum JackingActivityEventCollectionType {
  ExtOnly = 1,
  LoadOnly = 2,
  LoadAndExt = 3,
  Penetrations = 4,
  AirGap = 5,
  RPD = 6,
  CraneSlewBreak = 7,
  WaterDepthPerLeg = 8,
  WaterDepthSingle = 9,
  WaterTightIntegrity = 10,
  WaterTightIntegrityExtended = 11,
  SeaWaterSuction = 12,
  DavitPumps = 13,
  JettingPressure = 14
}

export class JackingActivityGuids {
  jackingArrivalPortOperationGuid: string;
  jackingArrivalOffshoreOperationGuid: string;

  jackDownOffshoreOperationGuid: string;
  jackDownPortOperationGuid: string;

  // obsolete guids, need to keep for compatibility with old completed days
  jackUpOffshoreOperationGuid: string;
  jackUpPortOperationGuid: string;
  jackingArrivalOperationGuid: string;
}

export class UpdatedJackingDataModel {
  jackingActivityEditors: JackingActivityEditor[];
  fieldPositioningEditors: FieldPositioningEditor[];
  onSiteMovementEditors: OnSiteMovementEditor[];
  setupChangeLogRecords: SetupChangeLogRecord[];
}

export class JackingArrivalPositioningFile {
  guid: string;
  name: string;
  extension: string;
  isUploaded?: boolean;
  // content is nulled once it replicates to backoffice to keep vessel db size trim, cant delete or dl once it has nulled
  hasContent: boolean;
}

export class JackingActivityRobFuelFigureEditor {
  id: number;
  jackingActivityId?: number | null;
  jackingFormInstanceId?: number | null;
  time: PtLuxonDate;
  timeOffset: number;
  dateTimeShow: string;
  jackingOpNumber: string;
  jackingOperationName: string;
  type: string;
  robFigure: number;
}



// WEBPACK FOOTER //
// ./src/models/projects/jacking-activity.models.ts